import React, { useEffect, useState } from "react";
import PopupModules from "src/features/auth/components/PopupModules";
import InputGroup from "../customer/InputGroup";
import { Button } from "src/business-types/grocery/components/Button";
import useEmployee from "src/business-types/grocery/hooks/useEmployee";
import { notifyError } from "src/assets/toastService";
import PermissionTable from "./PermissionTable";
import { EmployeeInterface } from "src/shared/services/interface";
import { useUser } from "src/business-types/grocery/components/UserContext";
import CommonButtons from "src/shared/components/CommonButtons";

type RolePermissions = Record<string, string[]>;

type Props = {
  open: boolean;
  onClose: () => void;
  editData?: EmployeeInterface | null;
  onSuccess?: () => void;
};

function EmployeeModal({ open, onClose, editData, onSuccess }: Props) {
  const { saveEmployee, changeEmployee } = useEmployee();
  const { user } = useUser();

  const [form, setForm] = useState({
    name: "",
    username: "",
    phone: "",
    email: "",
    customeraddress1: "",
    doj: "",
    role: "",

    role_permissions: {} as RolePermissions,
    additionalPermissions: {} as RolePermissions,
    removedPermissions: {} as RolePermissions,
    permissions: {} as RolePermissions,
  });

  const [disableBtn, setDisableBtn] = useState(false);

  const roleList = ["Admin", "User"];

  const [errors, setErrors] = useState({
    phone: "",
    email: "",
  });

  const [activeTab, setActiveTab] = useState<"user" | "permission">("user");

  /* ---------------- HELPERS ---------------- */

  const mergePermissions = (
    role: RolePermissions = {},
    additional: RolePermissions = {},
    removed: RolePermissions = {}
  ): RolePermissions => {
    const result: RolePermissions = {};

    const modules = new Set([
      ...Object.keys(role),
      ...Object.keys(additional),
      ...Object.keys(removed),
    ]);

    modules.forEach((module) => {
      const roleActions = role[module] || [];
      const additionalActions = additional[module] || [];
      const removedActions = removed[module] || [];

      let combined = new Set([...roleActions, ...additionalActions]);
      removedActions.forEach((a) => combined.delete(a));

      result[module] = Array.from(combined);
    });

    return result;
  };

  const splitPermissions = (
    final: RolePermissions,
    role: RolePermissions
  ) => {
    const additional: RolePermissions = {};
    const removed: RolePermissions = {};

    const modules = new Set([
      ...Object.keys(final),
      ...Object.keys(role),
    ]);

    modules.forEach((module) => {
      const finalActions = final[module] || [];
      const roleActions = role[module] || [];

      additional[module] = finalActions.filter(
        (a) => !roleActions.includes(a)
      );

      removed[module] = roleActions.filter(
        (a) => !finalActions.includes(a)
      );
    });

    return { additionalPermissions: additional, removedPermissions: removed };
  };

  /* ---------------- LOAD EDIT DATA ---------------- */

  useEffect(() => {
    setActiveTab("user");

    if (editData) {
      const rolePerm = editData.role_permissions || {};
      const additional = editData.additionalPermissions || {};
      const removed = editData.removedPermissions || {};

      setForm({
        name: editData.employee_name || "",
        username: editData.user_name || "",
        phone: editData.phone_number || "",
        email: editData.email_id || "",
        customeraddress1: editData.address_line_1 || "",
        doj: editData.date_of_joined || "",
        role: editData.role || "",

        role_permissions: rolePerm,
        additionalPermissions: additional,
        removedPermissions: removed,

        permissions: mergePermissions(rolePerm, additional, removed),
      });
    } else {
      reset();
    }
  }, [editData]);

  /* ---------------- INPUT CHANGE ---------------- */

  const handleChange = (name: string, value: string) => {
    let v = value;

    if (name === "phone") {
      v = v.replace(/\D/g, "");
      if (v.length < 8 || v.length > 12) {
        setErrors((p) => ({ ...p, phone: "Phone must be 8-12 digits" }));
      } else setErrors((p) => ({ ...p, phone: "" }));
    }

    if (name === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.(com|in|co)$/i;
      if (!emailRegex.test(v)) {
        setErrors((p) => ({ ...p, email: "Invalid email format" }));
      } else setErrors((p) => ({ ...p, email: "" }));
    }

    setForm((prev) => ({ ...prev, [name]: v }));
  };

  /* ---------------- VALIDATION ---------------- */

  const isFormValid = () =>
    form.name &&
    form.username &&
    form.phone &&
    form.email &&
    form.doj &&
    !errors.phone &&
    !errors.email;

  /* ---------------- SUBMIT ---------------- */

  const submit = () => {
    if (!isFormValid()) {
      notifyError("Please fix validation errors");
      return;
    }

    const { additionalPermissions, removedPermissions } = splitPermissions(
      form.permissions,
      form.role_permissions
    );

    if (editData) {
      const basePayload = {
        employee_name: form.name,
        user_name: form.username,
        phone_number: form.phone,
        email_id: form.email,
        address_line_1: form.customeraddress1,
        date_of_joined: form.doj,
        role: form.role,
      };

      const original = {
        employee_name: editData.employee_name,
        user_name: editData.user_name,
        phone_number: editData.phone_number,
        email_id: editData.email_id,
        address_line_1: editData.address_line_1,
        date_of_joined: editData.date_of_joined,
        role: editData.role,
      };

      const editPayload: Partial<typeof basePayload> & {
        additionalPermissions?: RolePermissions;
        removedPermissions?: RolePermissions;
      } = {};

      (Object.keys(basePayload) as (keyof typeof basePayload)[]).forEach(
        (key) => {
          if (basePayload[key] !== original[key]) {
            editPayload[key] = basePayload[key];
          }
        }
      );

      const hasPermissionChange =
        JSON.stringify(additionalPermissions) !==
        JSON.stringify(editData.additionalPermissions || {}) ||
        JSON.stringify(removedPermissions) !==
        JSON.stringify(editData.removedPermissions || {});

      if (hasPermissionChange) {
        editPayload.additionalPermissions = additionalPermissions;
        editPayload.removedPermissions = removedPermissions;
      }

      if (Object.keys(editPayload).length === 0) {
        notifyError("No changes detected");
        return;
      }

      setDisableBtn(true);

      changeEmployee(
        editData._id.$oid,
        editPayload,
        () => {
          setDisableBtn(false);
          onClose();
          reset();
          onSuccess?.();
        },
        () => setDisableBtn(false)
      );

      return;
    }

    setDisableBtn(true);

    saveEmployee(
      {
        employee_name: form.name,
        user_name: form.username,
        phone_number: form.phone,
        email_id: form.email,
        address_line_1: form.customeraddress1,
        date_of_joined: form.doj,
        role: form.role,
        additionalPermissions,
        removedPermissions,
        status: "inactive",
      },
      () => {
        setDisableBtn(false);
        onClose();
        reset();
        onSuccess?.();
      },
      () => setDisableBtn(false)
    );
  };

  /* ---------------- RESET ---------------- */

  const reset = () => {
    setForm({
      name: "",
      username: "",
      phone: "",
      email: "",
      customeraddress1: "",
      doj: "",
      role: "",
      role_permissions: {},
      additionalPermissions: {},
      removedPermissions: {},
      permissions: {},
    });
  };

  /* ---------------- UI ---------------- */

  return (
    <PopupModules
      isOpen={open}
      onClose={() => {
        onClose();
        reset();
      }}
      title={editData ? "Edit Employee" : "Add Employee"}
      className="w-[60%] h-fit"
    >
      <div className="py-2 w-full h-[85%]">
        <div className="flex border-b mb-4">
          <button
            className={`px-4 py-2 text-sm ${activeTab === "user"
                ? "border-b-2 border-blue-500 text-blue-600"
                : "text-gray-500"
              }`}
            onClick={() => setActiveTab("user")}
          >
            User Details
          </button>

          {editData && (
            <button
              className={`px-4 py-2 text-sm ${activeTab === "permission"
                  ? "border-b-2 border-blue-500 text-blue-600"
                  : "text-gray-500"
                }`}
              onClick={() => setActiveTab("permission")}
            >
              Permissions
            </button>
          )}
        </div>

        {activeTab === "user" && (
          <div className="grid grid-cols-2 gap-4">
            <InputGroup label="Name" type="text" value={form.name} name="name" onChange={handleChange} mandatory />
            <InputGroup label="Username" type="text" value={form.username} name="username" onChange={handleChange} mandatory disabled={!!editData} />

            <div>
              <InputGroup label="Phone" type="number" value={form.phone} name="phone" onChange={handleChange} mandatory />
              {errors.phone && <p className="text-red-500 text-sm">{errors.phone}</p>}
            </div>

            <div>
              <InputGroup label="Email" type="email" value={form.email} name="email" onChange={handleChange} mandatory />
              {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
            </div>

            <InputGroup label="Date Of Joining" type="date" value={form.doj} name="doj" onChange={handleChange} mandatory />

            <select
              value={form.role}
              onChange={(e) => handleChange("role", e.target.value)}
              className="border rounded-md px-3 py-2 text-sm"
            >
              <option value="">Select Role</option>
              {roleList.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
        )}

        {activeTab === "permission" && editData && (
          <PermissionTable
            permissions={form.permissions}
            setPermissions={(data) =>
              setForm((prev) => ({
                ...prev,
                permissions:
                  typeof data === "function"
                    ? data(prev.permissions)
                    : data,
              }))
            }
          />
        )}
      </div>

      <div className="flex justify-end gap-4 pb-4">
        <CommonButtons label="Cancel" variant="outlineDanger" onClick={() => { onClose(); reset(); }} />
        <CommonButtons label={editData ? "Update" : "Confirm"} onClick={submit} disabled={!isFormValid() || disableBtn} />
      </div>
    </PopupModules>
  );
}

export default EmployeeModal;